import GenericTable from "components/table";
import { Role } from "types";
import React from "react";
import { useAuthContext, User } from "auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import { Trash } from "lucide-react";
import { Dialog } from "components/dialog";
import { mockUsers } from "../variables";
import { useConfirmation } from "components/dialog/confirmation";
import api from "api/axios";
import { handleAxiosAndCaughtError } from "utils";
import { CellContext } from "@tanstack/react-table";
import { PERMISSIONS } from "auth/permissions";
import dayjs from "dayjs";
import { DATE_TIME_FORMAT } from "constants/date";
import { useTranslation } from "react-i18next";

type RoleTableProps = {
  tableData: Role[];
  fetchRoles: () => Promise<void>;
};

const RoleTable: React.FC<RoleTableProps> = ({ tableData, fetchRoles }) => {
  const { t } = useTranslation();
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const { hasPermission } = useAuthContext();
  const { confirm } = useConfirmation();

  const permissions = {
    canAddRole: hasPermission(PERMISSIONS.ADD_ROLE),
    canDeleteRole: hasPermission(PERMISSIONS.DELETE_ROLE),
  };

  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [roleName, setRoleName] = React.useState("");
  const [roleDesc, setRoleDesc] = React.useState("");
  const [roleUsers, setRoleUsers] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [userSearchTerm, setUserSearchTerm] = React.useState("");

  const handleDeleteRow = async (id: string) => {
    const confirmed = await confirm({
      title: t("acl.deleteRole"),
      message: t("acl.deleteRoleMessage"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      // Perform the actual deletion

      setDeletingRowId(id);
      try {
        // TODO: temporary for backward compatibility
        await api.delete(`/groups/${id}`);
        await fetchRoles();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setDeletingRowId(null);
      }
    }
  };

  const handleAddRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // TODO: temporary for backward compatibility
      await api.post(`/groups`, {
        name: roleName,
        description: roleDesc,
      });
      await fetchRoles();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsSubmitting(false);
      setIsAddDialogOpen(false);
      setRoleName("");
      setRoleDesc("");
      setRoleUsers([]);
      setUserSearchTerm("");
    }
  };

  const handleUserToggle = (userId: string) => {
    setRoleUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const removeUser = (userId: string) => {
    setRoleUsers((prev) => prev.filter((id) => id !== userId));
  };

  const filteredUsers = mockUsers.filter(
    (user: User) =>
      user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const selectedUsers = mockUsers.filter((user: User) =>
    roleUsers.includes(user.id)
  );

  const columns = [
    {
      accessorKey: "name",
      header: () => <p>{t("common.name")}</p>,
      cell: (info: CellContext<Role, string>) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white ">
          {info.getValue()}
        </p>
      ),
    },
    {
      accessorKey: "description",
      header: () => <p>{t("common.description")}</p>,
      cell: (info: CellContext<Role, string>) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    },
    {
      accessorKey: "num_of_members",
      header: () => <p>{t("library.members")}</p>,
      cell: (info: CellContext<Role, number>) => {
        const numOfMembers = info.getValue() || "0";

        if (numOfMembers?.toString() === "0") {
          return (
            <p className="text-sm font-normal text-gray-400 dark:text-gray-500">
              {t("library.noMembers")}
            </p>
          );
        }

        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {`${numOfMembers} ${t("library.members")}`}
          </p>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: () => <p>{t("library.createdDate")}</p>,
      cell: (info: CellContext<Role, string>) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {dayjs(info.getValue()).format(DATE_TIME_FORMAT)}
        </p>
      ),
    },
    {
      accessorKey: "id",
      header: () => (
        <p className="text-left font-medium text-gray-900 dark:text-gray-100"></p>
      ),
      cell: (info: CellContext<Role, string>) => (
        <div className="flex items-center gap-2">
          {permissions.canDeleteRole && (
            <button
              className="rounded p-2 hover:bg-gray-100 dark:hover:bg-navy-700"
              title={t("roles.removeRole")}
              onClick={() => {
                handleDeleteRow(info.getValue());
              }}
              disabled={deletingRowId === info.getValue()}
            >
              <Trash size={15} className="text-red-500" />
            </button>
          )}
        </div>
      ),
      meta: {
        cellClassName: "min-w-[50px]",
        headerClassName: "min-w-[50px]",
      },
    },
  ];
  const dataWithActions = tableData.map((row) => ({
    ...row,
    deletingRowId,
    handleDeleteRow,
  }));

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return deletingRowId === rowId ? "animate-pulse opacity-60" : "";
  };
  return (
    <>
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        searchPlaceholder={t("roles.searchRoles")}
        debug={true}
        onAdd={
          permissions.canAddRole ? () => setIsAddDialogOpen(true) : undefined
        }
        onRowClick={(item) => {
          navigate(`/dashboard/roles/${item.id}`);
        }}
        renderRowClassName={renderRowClassName}
        addLabel={t("roles.createNewRole")}
      />
      <Dialog
        subHeader={t("roles.createDescription")}
        header={t("roles.createTitle")}
        isOpen={isAddDialogOpen}
        setIsOpen={setIsAddDialogOpen}
      >
        <form
          onSubmit={handleAddRole}
          className="flex max-h-[70vh] flex-col gap-6 p-2"
        >
          <div className="space-y-6">
            <div className="flex flex-col gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("roles.nameLabel")}
                </label>
                <input
                  type="text"
                  className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm placeholder-gray-400 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  required
                  placeholder={t("roles.namePlaceholder")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("common.description")}
                </label>
                <textarea
                  rows={3}
                  className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm placeholder-gray-400 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                  value={roleDesc}
                  onChange={(e) => setRoleDesc(e.target.value)}
                  placeholder={t("roles.descriptionPlaceholder")}
                />
              </div>
            </div>

            {/* Select members */}
            {/* <div>
              <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Add Members *
              </label>

              {selectedUsers.length > 0 && (
                <div className="mb-4 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <div className="flex flex-wrap gap-2">
                    {selectedUsers.slice(0, 3).map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center gap-2 rounded-full bg-brand-100 px-3 py-1.5 text-sm font-medium text-brand-800 dark:bg-brand-900 dark:text-brand-200"
                      >
                        <img
                          className="h-5 w-5 rounded-full"
                          src={avatar}
                          alt={user.name}
                        />
                        <span>{user.name}</span>
                        <button
                          type="button"
                          onClick={() => removeUser(user.id)}
                          className="ml-1 rounded-full p-0.5 transition-colors hover:bg-brand-200 dark:hover:bg-brand-800"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    {selectedUsers.length > 3 && (
                      <div className="flex items-center gap-2 rounded-full bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                        <span>+{selectedUsers.length - 3} more</span>
                        <button
                          type="button"
                          onClick={() => {
                            const remainingUsers = selectedUsers
                              .slice(3)
                              .map((u) => u.name)
                              .join(", ");
                            alert(`Other selected users: ${remainingUsers}`);
                          }}
                          className="ml-1 rounded-full p-0.5 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
                          title="View all selected users"
                        >
                          <UserIcon size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {selectedUsers.length} member
                    {selectedUsers.length !== 1 ? "s" : ""} selected
                  </p>
                </div>
              )}

              <div className="relative">
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm placeholder-gray-400 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                    placeholder="Search users by name or email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    onFocus={() => setIsUserDropdownOpen(true)}
                  />
                </div>
                {isUserDropdownOpen && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => {
                        const isSelected = roleUsers.includes(user.id);
                        return (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleUserToggle(user.id)}
                            className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                              isSelected
                                ? "dark:bg-brand-900/50 bg-brand-50"
                                : ""
                            }`}
                          >
                            <img
                              className="h-8 w-8 rounded-full"
                              src={avatar}
                              alt={user.name}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                {user.name}
                              </p>
                              <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                                {user.email}
                              </p>
                            </div>
                            {isSelected && (
                              <Check className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        No users found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {isUserDropdownOpen && (
                <div
                  className="fixed inset-0 z-0"
                  onClick={() => setIsUserDropdownOpen(false)}
                />
              )}
            </div> */}
          </div>

          <div className="flex justify-end gap-3 pt-6">
            <button
              type="button"
              onClick={() => setIsAddDialogOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white" />
                  {t("common.creating")}
                </div>
              ) : (
                t("roles.createRole")
              )}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default RoleTable;
